import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  RefreshCcw, Loader2, Lightbulb, Gift, Clock,
  Users, UserPlus, MessageSquare, Crown, Hourglass,
  ChevronDown, ChevronUp, Trophy, BarChart3
} from 'lucide-react';
import { API_URL } from '../utils/apiUrl';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, Area, ComposedChart, Line
} from 'recharts';

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCompactCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  if (value >= 100000) {
    return `₹${(value / 100000).toFixed(1)}L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  }
  return `₹${value.toFixed(0)}`;
};

// Custom tooltip component for chart
const ChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const gapValue = data.target - data.actual;
    const isShort = gapValue > 0;
    
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">Day {data.day}</p>
        <div className="space-y-1 text-sm">
          <p className="text-blue-400">Target: {formatCurrency(data.target)}</p>
          <p className="text-emerald-400">Actual: {formatCurrency(data.actual)}</p>
          <p className={isShort ? 'text-red-400' : 'text-emerald-400'}>
            {isShort ? 'Shortfall' : 'Surplus'}: {formatCurrency(Math.abs(gapValue))}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

// Legend formatter
const legendFormatter = (value) => <span style={{ color: '#94a3b8' }}>{value}</span>;

const FocusSnapshotWidget = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [showBrokerageChart, setShowBrokerageChart] = useState(false);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/targets/focus-snapshot`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSnapshot(data);
      } else {
        setError('Failed to load snapshot');
      }
    } catch (err) {
      console.error('Error fetching snapshot:', err);
      setError('Failed to load snapshot');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short'
      });
    } catch {
      return '-';
    }
  };

  const calculateDaysLeft = (deadlineDate) => {
    if (!deadlineDate) return 0;
    const deadline = new Date(deadlineDate);
    const today = new Date();
    return Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <Card className="card-dark border-[#C5A059]/30 bg-gradient-to-br from-slate-900 to-slate-800">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
        </CardContent>
      </Card>
    );
  }

  if (error || !snapshot) {
    return (
      <Card className="card-dark border-white/10">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-slate-400">{error || 'No snapshot available'}</p>
          <Button variant="ghost" onClick={fetchSnapshot} className="mt-2">
            <RefreshCcw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasShortfalls = (snapshot.action_items?.length || 0) > 0;
  const hasIncentives = snapshot.incentive_brokerage_qualified || snapshot.incentive_meeting_qualified;
  
  // Brokerage Gap data
  const gap = snapshot.brokerage_gap || {};
  const isSurplus = gap.is_surplus;
  const dailyProgress = gap.daily_progress || [];

  return (
    <Card className="card-dark border-[#C5A059]/30 bg-gradient-to-br from-slate-900 via-slate-900 to-[#C5A059]/5" data-testid="focus-snapshot-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#C5A059]" />
            Focus Advisory Snapshot
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-slate-600 text-slate-400">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(snapshot.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Badge>
            <Button variant="ghost" size="sm" onClick={fetchSnapshot} className="h-8 w-8 p-0">
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Action Items Section */}
        {snapshot.action_items?.length > 0 && (
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#C5A059]" />
              Today&apos;s Focus
            </h3>
            <ul className="space-y-2">
              {snapshot.action_items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="text-[#C5A059] mt-1">•</span>
                  <span className="text-slate-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* No Shortfalls - All on Track */}
        {!hasShortfalls && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <h3 className="text-emerald-400 font-medium">All Targets On Track!</h3>
                <p className="text-slate-400 text-sm">Keep up the great work today.</p>
              </div>
            </div>
          </div>
        )}

        {/* ========== BROKERAGE GAP SECTION (Clickable) ========== */}
        {gap.monthly_target > 0 && (
          <div 
            className={`rounded-lg border cursor-pointer transition-all duration-200 hover:border-opacity-60 ${
              isSurplus 
                ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15' 
                : 'bg-red-500/10 border-red-500/30 hover:bg-red-500/15'
            }`}
            onClick={() => setShowBrokerageChart(!showBrokerageChart)}
            data-testid="brokerage-gap-section"
          >
            {/* Summary Header - Always Visible */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-white font-medium">Brokerage Gap</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-slate-600 text-slate-400">
                    {gap.month_name}
                  </Badge>
                  <Badge className={isSurplus ? 'bg-emerald-600' : 'bg-red-600'}>
                    Trading Day {gap.trading_days_elapsed ?? gap.day_of_month} / {gap.trading_days_in_month ?? '-'}
                  </Badge>
                  {showBrokerageChart ? (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Status Indicator */}
              <div className="flex items-center gap-2 mb-3">
                {isSurplus ? (
                  <>
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span className="text-emerald-400 font-medium">Ahead of Target!</span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <span className="text-red-400 font-medium">Behind Target</span>
                  </>
                )}
              </div>

              {/* Summary Line - Today's Target Breakdown */}
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs mb-1 text-center">Today&apos;s Target to Generate</p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className={`text-sm ${isSurplus ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isSurplus ? 'Surplus' : 'Backlog'}: {formatCurrency(isSurplus ? gap.surplus : gap.backlog)}
                  </span>
                  <span className="text-slate-500">+</span>
                  <span className="text-blue-400 text-sm">Daily: {formatCurrency(gap.daily_target)}</span>
                  <span className="text-slate-500">=</span>
                  <span className="text-white font-bold text-lg">{formatCurrency(gap.total_to_generate_today)}</span>
                  {isSurplus && <span className="text-yellow-500">🏆</span>}
                </div>
              </div>

              {/* Quick Stats Row */}
              <div className="flex justify-between mt-3 text-xs">
                <div>
                  <span className="text-slate-400">Monthly: </span>
                  <span className="text-white">{formatCurrency(gap.monthly_target)}</span>
                </div>
                <div>
                  <span className="text-slate-400">Running: </span>
                  <span className="text-blue-400">{formatCurrency(gap.running_target)}</span>
                </div>
                <div>
                  <span className="text-slate-400">Actual: </span>
                  <span className="text-emerald-400">{formatCurrency(gap.actual_mtd)}</span>
                </div>
              </div>

              {/* Click hint */}
              {!showBrokerageChart && (
                <p className="text-slate-500 text-xs text-center mt-3">
                  Click to view progress chart →
                </p>
              )}
            </div>

            {/* Expanded Chart Section */}
            {showBrokerageChart && dailyProgress.length > 0 && (
              <div className="border-t border-slate-700/50 p-4" data-testid="brokerage-chart">
                <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#C5A059]" />
                  Target vs Actual - {gap.month_name}
                </h4>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={dailyProgress} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="day" 
                        stroke="#94a3b8" 
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                        tickFormatter={(day) => `${day}`}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                        tickFormatter={formatCompactCurrency}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        formatter={legendFormatter}
                      />
                      
                      {/* Target Line - Blue dashed */}
                      <Line 
                        type="monotone" 
                        dataKey="target" 
                        name="Target (Cumulative)"
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                      
                      {/* Actual Area - RED if shortfall, GREEN if surplus */}
                      <Area
                        type="monotone"
                        dataKey="actual"
                        name="Actual (Cumulative)"
                        fill={isSurplus ? "url(#colorSurplus)" : "url(#colorShortfall)"}
                        stroke={isSurplus ? "#10b981" : "#ef4444"}
                        strokeWidth={2}
                      />
                      
                      {/* Gradient definitions */}
                      <defs>
                        <linearGradient id="colorSurplus" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorShortfall" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Daily Progress Table */}
                <div className="mt-4 max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="text-slate-400 text-xs">
                        <th className="text-left py-2 px-2">Day</th>
                        <th className="text-right py-2 px-2">Target</th>
                        <th className="text-right py-2 px-2">Actual</th>
                        <th className="text-right py-2 px-2">Gap</th>
                        <th className="text-center py-2 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyProgress.map((day) => (
                        <tr key={day.day} className="border-t border-slate-700/50">
                          <td className="py-2 px-2 text-white">{day.day}</td>
                          <td className="py-2 px-2 text-right text-blue-400">{formatCompactCurrency(day.target)}</td>
                          <td className="py-2 px-2 text-right text-emerald-400">{formatCompactCurrency(day.actual)}</td>
                          <td className={`py-2 px-2 text-right ${day.status === 'surplus' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {day.status === 'surplus' ? '+' : '-'}{formatCompactCurrency(Math.abs(day.gap))}
                          </td>
                          <td className="py-2 px-2 text-center">
                            {day.status === 'surplus' ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-500">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500">✗</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {snapshot.traded_clients_shortfall > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <Users className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Clients to Trade</p>
              <p className="text-white font-bold text-sm">{snapshot.traded_clients_shortfall}</p>
            </div>
          )}
          {snapshot.clients_to_activate > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <UserPlus className="w-4 h-4 text-purple-500 mx-auto mb-1" />
              <p className="text-xs text-slate-400">To Activate</p>
              <p className="text-white font-bold text-sm">{snapshot.clients_to_activate}</p>
            </div>
          )}
          {snapshot.tier_focus && (
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <Crown className="w-4 h-4 text-cyan-500 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Tier Focus</p>
              <p className="text-white font-bold text-sm">{snapshot.tier_focus}</p>
            </div>
          )}
          {snapshot.new_clients_shortfall > 0 && (
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <UserPlus className="w-4 h-4 text-green-500 mx-auto mb-1" />
              <p className="text-xs text-slate-400">New Clients</p>
              <p className="text-white font-bold text-sm">{snapshot.new_clients_shortfall}</p>
            </div>
          )}
          {/* Meetings */}
          {(snapshot.meetings_shortfall > 0 || snapshot.shortfalls?.monthly?.meetings) && (
            <div className={`rounded-lg p-3 text-center ${snapshot.meetings_shortfall > 0 ? 'bg-slate-800/50' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
              <MessageSquare className={`w-4 h-4 mx-auto mb-1 ${snapshot.meetings_shortfall > 0 ? 'text-cyan-500' : 'text-emerald-500'}`} />
              <p className="text-xs text-slate-400">Meetings</p>
              {snapshot.meetings_shortfall > 0 ? (
                <p className="text-white font-bold text-sm">{snapshot.meetings_shortfall} to go</p>
              ) : (
                <p className="text-emerald-400 font-bold text-sm">
                  {snapshot.shortfalls?.monthly?.meetings?.achieved || 0}/{snapshot.shortfalls?.monthly?.meetings?.target || 0} ✓
                </p>
              )}
            </div>
          )}
          {/* Meeting Incentive */}
          <div className={`rounded-lg p-3 text-center ${snapshot.incentive_meeting_qualified ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50'}`}>
            <Gift className={`w-4 h-4 mx-auto mb-1 ${snapshot.incentive_meeting_qualified ? 'text-emerald-500' : 'text-amber-500'}`} />
            <p className="text-xs text-slate-400">Meeting Incentive</p>
            {snapshot.incentive_meeting_qualified ? (
              <p className="text-emerald-400 font-bold text-sm">{formatCurrency(snapshot.incentive_meeting)}</p>
            ) : (
              <p className="text-white font-bold text-sm">{snapshot.incentive_meeting_remaining} to qualify</p>
            )}
          </div>
          {/* Pending Activations */}
          <div className={`rounded-lg p-3 text-center ${snapshot.pending_activations?.length > 0 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-slate-800/50'}`}>
            <Hourglass className={`w-4 h-4 mx-auto mb-1 ${snapshot.pending_activations?.length > 0 ? 'text-yellow-500' : 'text-slate-500'}`} />
            <p className="text-xs text-slate-400">Pending Activations</p>
            <p className={`font-bold text-sm ${snapshot.pending_activations?.length > 0 ? 'text-yellow-400' : 'text-white'}`}>
              {snapshot.pending_activations?.length || 0}
            </p>
          </div>
        </div>

        {/* Incentive Alerts Section */}
        {snapshot.incentive_alerts?.length > 0 && (
          <div className={`rounded-lg p-4 ${hasIncentives ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
            <h3 className={`font-medium mb-3 flex items-center gap-2 ${hasIncentives ? 'text-emerald-400' : 'text-amber-400'}`}>
              <Gift className={`w-4 h-4 ${hasIncentives ? 'text-emerald-500' : 'text-amber-500'}`} />
              Incentive Alerts
            </h3>
            <ul className="space-y-2">
              {snapshot.incentive_alerts.map((alert, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className={hasIncentives ? 'text-emerald-500' : 'text-amber-500'}>•</span>
                  <span className="text-slate-300">{alert}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Brokerage Incentive Card */}
        <div className={`rounded-lg p-3 ${snapshot.incentive_brokerage_qualified ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs">Brokerage Incentive</span>
            {snapshot.incentive_brokerage_qualified ? (
              <Badge className="bg-emerald-600 text-xs">Qualified</Badge>
            ) : (
              <Badge variant="outline" className="border-slate-600 text-xs">Pending</Badge>
            )}
          </div>
          {snapshot.incentive_brokerage_qualified ? (
            <p className="text-emerald-400 font-bold">{formatCurrency(snapshot.incentive_brokerage)}</p>
          ) : (
            <p className="text-slate-400 text-sm">{formatCurrency(snapshot.incentive_brokerage_remaining)} to qualify</p>
          )}
        </div>

        {/* Pending Activations Detail Card */}
        {snapshot.pending_activations?.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4" data-testid="pending-activations-card">
            <h3 className="text-yellow-300 font-medium mb-3 flex items-center gap-2">
              <Hourglass className="w-4 h-4" />
              Pending Activations Detail
            </h3>
            <p className="text-yellow-200/70 text-xs mb-3">
              Meeting completed, awaiting brokerage within activation window
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {snapshot.pending_activations.slice(0, 6).map((p, idx) => {
                const daysLeft = calculateDaysLeft(p.deadline_date);
                
                return (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg border border-white/5"
                  >
                    <div>
                      <p className="text-sm text-white font-medium">
                        {p.client_name || p.client_code}
                        {p.client_name && <span className="text-slate-500 ml-1">({p.client_code})</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        Meeting: {formatDate(p.meeting_date)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      daysLeft <= 7 ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      daysLeft <= 14 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`} data-testid={`focus-pending-days-badge-${idx}`}>
                      {daysLeft} days left
                    </span>
                  </div>
                );
              })}
            </div>
            {snapshot.pending_activations.length > 6 && (
              <p className="text-yellow-300/70 text-xs mt-2 text-center">
                +{snapshot.pending_activations.length - 6} more
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FocusSnapshotWidget;
